import {Layout} from '@components/layout/index';
import {
  attachInventoryAndPreOrderInfoToProductRows,
  getInventoryForSkuListWithPreOrderInfoCanHandleBundles
} from '@helpers/products/get-inventory.mjs';
import {useSession} from 'next-auth/react';
import {getAllCollectionsSlugs} from '@helpers/get-menu.mjs';
import {DesktopCollectionPageComponent} from '@components/collections/CollectionPageDesktop';
import {MobileCollectionPageComponent} from '@components/collections/CollectionPageMobile';
import {CollectionProductsPageMobile} from '@components/collections/CollectionProductsMobile.jsx';
import {fixPropsForJson} from '@helpers/common/json.mjs';
import {finalizeProductPrices} from '@helpers/config-master.mjs';
import {Breadcrumbs} from '@components/product/global/Breadcrumbs.jsx';
import {CollectionProductsPageDesktop} from '@components/collections/CollectionProductsDesktop';
import {GenerateCollectionPageDynamicSlugJsonLd} from '@components/json-ld/item-list';
import {getCategoryInfo} from '@helpers/collections/collection-page-product-data.mjs';

export const getStaticPaths = () => {
  let menuJsonPaths = getAllCollectionsSlugs();
  const pathsToExclude = [
    '"',
    'active-pa-systems',
    'cyber-monday',
    'car-audio-batteries',
    'marine-subwoofers',
    'sound-deadening',
    'vehicle-specific-subwoofer-enclosures',
    'professional-audio'
  ];
  menuJsonPaths = menuJsonPaths?.filter((x) => !pathsToExclude?.some((path) => x?.includes(path)));

  return {
    paths: menuJsonPaths,
    fallback: 'blocking'
  };
};

export const getStaticProps = async ({params}) => {
  const collectionPageSlug = params?.slug?.join('/');
  // console.log('[getStaticProps]:', {collectionPageSlug})
  const categoryData = await getCategoryInfo(collectionPageSlug);
  // console.dir({categoryData}, {depth: null});
  if (categoryData?.notFound) {
    return {
      notFound: true
    };
  }

  const category = categoryData?.category;
  const collection = categoryData?.collection;

  let products = categoryData?.products;
  if (products?.length) {
    const skuList = products?.map((product) => product?.sku);
    const invWPreOrderInfoForSkuList =
      await getInventoryForSkuListWithPreOrderInfoCanHandleBundles(skuList);
    products = attachInventoryAndPreOrderInfoToProductRows(products, invWPreOrderInfoForSkuList);
  }
  // console.dir({getStaticProps_products: products}, {depth: null});

  const props = {
    props: {
      slug: collectionPageSlug,
      category: category ? fixPropsForJson(category) : null,
      collection: collection ? fixPropsForJson(collection) : null,
      products: products?.length > 0 ? fixPropsForJson(products) : null,
      paddingX: false,
      showGeoTransitBanner: true,
      showSaleBanner: true,
      showFooterSignup: true,
      showPushNotificationPopup: true
    },
    revalidate: 1800
  };

  return props;
};

export default function Collections({slug, category, collection, products}) {
  const {data: session, status} = useSession();
  const sessionIsLoading = status === 'loading';
  const sessionIsLoaded = sessionIsLoading !== true;
  const isLoggedIn = session && !!session?.user?.email;

  // console.dir({collectionProducts: collection}, {depth: null});

  const elements = collection?.['sub-collections'] ?? category?.collections; // category is last as we may have a deep slug
  const collectionDbRow = collection?.dbRow;
  // console.log({collectionDbRow})
  const title =
    collectionDbRow?.collection_page_title ??
    collection?.category ??
    collection?.title ??
    category?.category;
  const subtitle = collection?.subtitle;

  let collectionDescription = (collectionDbRow?.collection_seo_description ?? '')?.substring(
    0,
    320
  );

  products = sort_6_8_10_Inch_Speakers_InOrder(products);
  products = finalizeProductPrices(products);

  const hasNoProducts = !products?.length;
  const hasProducts = !!products?.length;
  // console.log('[Collections] hasNoProducts:', hasNoProducts);

  return (
    <div className="md:max-w-[1300px] m-auto relative pt-1">
      <div className="px-3">
        <CollectionBreadcrumbs category={category} collection={collection} />
      </div>

      {/* Using hidden divs vs const isMobile = useMobileOrTablet() is the only way to prevent CLS issues */}
      <div className={'hidden md:block'}>
        {hasNoProducts && (
          <DesktopCollectionPageComponent
            slug={slug}
            title={title}
            description={collectionDescription}
            collections={elements}
          />
        )}

        {hasProducts && (
          <CollectionProductsPageDesktop
            slug={slug}
            category={category}
            collection={collection}
            title={title}
            products={products}
            session={session}
            description={collectionDescription}
          />
        )}
      </div>

      <div className={'md:hidden'}>
        {hasNoProducts && (
          <MobileCollectionPageComponent slug={slug} title={title} collections={elements} />
        )}

        {hasProducts && (
          <CollectionProductsPageMobile
            slug={slug}
            category={category}
            title={title}
            subtitle={subtitle}
            products={products}
          />
        )}
      </div>

      <GenerateCollectionPageDynamicSlugJsonLd products={products} />
    </div>
  );
}
Collections.Layout = Layout;

const CollectionBreadcrumbs = ({category, collection}) => {
  const categoryLink = category?.handle;
  const categoryName = category?.category;
  const collectionLink = collection?.slug;
  const collectionName = collection?.title;

  // console.log('[CollectionBreadcrumbs]:', {
  //   categoryName,
  //   categoryLink,
  //   collectionLink,
  //   collectionName
  // });

  return (
    <Breadcrumbs
      categoryLink={categoryLink}
      categoryName={categoryName}
      collectionLink={collectionLink}
      collectionName={collectionName}
      excludeCategoryLevel={false}
    />
  );
};

export const sort_6_8_10_Inch_Speakers_InOrder = (products) => {
  if (!products?.length) return [];

  const sizeOrder = {
    '6.5"': 1,
    '8"': 2,
    '10"': 3
  };

  const getSize = (sku) => {
    if (sku?.includes('65')) return '6.5"';
    if (sku?.includes('8')) return '8"';
    if (sku?.includes('10')) return '10"';
    return '';
  };

  return products?.sort((a, b) => sizeOrder[getSize(a.sku)] - sizeOrder[getSize(b.sku)]);
};
